import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Trash2, Edit3, Eye } from "lucide-react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";

const TAGS = ["Galaxie", "Nébuleuse", "Lune", "Soleil", "Voie lactée", "Amas ouvert", "Amas globulaire", "Champ large"];

export default function Library() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tag, setTag] = useState(null);
  const [mode, setMode] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/images", { params: { q: q || undefined, tag: tag || undefined, mode: mode || undefined } });
      setItems(data);
    } catch (e) {
      toast.error("Impossible de charger la médiathèque");
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tag, mode]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const onDelete = async (id) => {
    if (!window.confirm("Supprimer cette image ?")) return;
    await api.delete(`/images/${id}`);
    toast.success("Supprimée");
    load();
  };

  const onRename = async (id, name) => {
    await api.patch(`/images/${id}`, { name });
    setEditing(null);
    toast.success("Renommée");
    load();
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Médiathèque</div>
          <h1 className="mt-2 text-3xl sm:text-4xl font-light tracking-tight">Vos images traitées</h1>
        </div>
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher par nom…"
            className="w-full bg-secondary/40 border border-border rounded-md py-2 pl-10 pr-3 text-sm outline-none focus:border-primary"
            data-testid="library-search"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="label-eyebrow mr-1">Tags :</span>
        <button onClick={() => setTag(null)} className={`px-3 py-1 rounded-md text-xs border ${!tag ? "bg-primary/15 border-primary text-white" : "border-white/10 text-muted-foreground hover:text-white"}`} data-testid="tag-all">Tous</button>
        {TAGS.map((t) => (
          <button key={t} onClick={() => setTag(t === tag ? null : t)} className={`px-3 py-1 rounded-md text-xs border ${t === tag ? "bg-primary/15 border-primary text-white" : "border-white/10 text-muted-foreground hover:text-white"}`} data-testid={`tag-${t}`}>{t}</button>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="label-eyebrow mr-1">Mode :</span>
        {[
          ["naturel", "Naturel"],
          ["ciel_profond", "Ciel Profond"],
          ["details", "Détails"],
          ["anti_bruit", "Anti-Bruit"],
          ["observatoire", "Observatoire"],
        ].map(([k, label]) => (
          <button key={k} onClick={() => setMode(k === mode ? null : k)} className={`px-3 py-1 rounded-md text-xs border ${k === mode ? "bg-primary/15 border-primary text-white" : "border-white/10 text-muted-foreground hover:text-white"}`} data-testid={`filter-mode-${k}`}>{label}</button>
        ))}
      </div>

      {items.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-16 text-center text-muted-foreground">
          Aucune image. <Link to="/" className="text-violet-300 underline-offset-2 hover:underline">Sublimez votre première astrophoto.</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="library-grid">
          {items.map((it) => (
            <div key={it.id} className="group rounded-md border border-white/10 bg-card overflow-hidden hover:-translate-y-[2px] transition-transform">
              <div className="relative aspect-[4/3] bg-black">
                <img src={imageUrl(it.id, "processed")} alt={it.name} className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/60 backdrop-blur text-[11px] font-mono">{it.score}/100</div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity grid place-items-center">
                  <Link to={`/image/${it.id}`} className="px-3 py-1.5 rounded-md bg-white/10 border border-white/20 backdrop-blur-md text-xs inline-flex items-center gap-2" data-testid={`view-${it.id}`}>
                    <Eye className="w-3.5 h-3.5" /> Ouvrir
                  </Link>
                </div>
              </div>
              <div className="p-3">
                {editing === it.id ? (
                  <input
                    autoFocus
                    defaultValue={it.name}
                    onBlur={(e) => onRename(it.id, e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") onRename(it.id, e.target.value); if (e.key === "Escape") setEditing(null); }}
                    className="w-full bg-secondary/40 border border-border rounded px-2 py-1 text-sm"
                    data-testid={`rename-input-${it.id}`}
                  />
                ) : (
                  <div className="font-medium text-sm truncate">{it.name}</div>
                )}
                <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
                  <span>{it.mode_label}</span>
                  <span className="font-mono">{new Date(it.created_at).toLocaleDateString("fr-FR")}</span>
                </div>
                <div className="mt-3 flex gap-1">
                  <button onClick={() => setEditing(it.id)} className="flex-1 text-[11px] py-1 rounded border border-white/10 hover:bg-white/5 inline-flex items-center justify-center gap-1" data-testid={`edit-${it.id}`}><Edit3 className="w-3 h-3" />Renommer</button>
                  <button onClick={() => onDelete(it.id)} className="flex-1 text-[11px] py-1 rounded border border-white/10 hover:bg-destructive/20 hover:border-destructive inline-flex items-center justify-center gap-1" data-testid={`delete-${it.id}`}><Trash2 className="w-3 h-3" />Supprimer</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
